import { supabase } from '../lib/supabase';

/**
 * Gallery Service - Handles fetching images from the Supabase gallery bucket
 */

/**
 * Fetches all images from the gallery bucket
 * @returns {Promise<Array>} Array of image URLs with metadata
 */
export const getGalleryImages = async () => {
  try {
    const { data, error } = await supabase?.storage?.from('gallery')?.list('', {
        limit: 100,
        offset: 0,
        sortBy: { column: 'name', order: 'asc' }
      });

    if (error) {
      console.error('Error fetching gallery images:', error);
      throw error;
    }

    // Filter for PNG files and map to full URLs
    const imageUrls = data?.filter(file => file?.name?.toLowerCase()?.endsWith('.png'))?.map(file => {
        const { data: { publicUrl } } = supabase?.storage?.from('gallery')?.getPublicUrl(file?.name);
        
        return {
          name: file?.name,
          url: publicUrl,
          // Extract feature name from filename (e.g., "feature-01-schedule" from "feature-01-schedule.png")
          featureName: file?.name?.replace('.png', '')?.replace(/feature-\d+-/, '')?.split('-')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ')
        };
      });

    return imageUrls;
  } catch (error) {
    console.error('Error in getGalleryImages:', error);
    throw error;
  }
};

/**
 * Gets feature slideshow data with images from gallery bucket
 * @returns {Promise<Array>} Array of slide objects with images and descriptions
 */
export const getFeatureSlideshow = async () => {
  try {
    const images = await getGalleryImages();
    
    // Map images to slideshow data with descriptions
    const slideshowData = images?.map((image, index) => ({
      id: index + 1,
      image: image?.url,
      title: image?.featureName || `Feature ${index + 1}`,
      description: getFeatureDescription(image?.name),
      alt: `StillFlow feature showcasing ${image?.featureName || 'planner functionality'}`
    }));

    return slideshowData;
  } catch (error) {
    console.error('Error in getFeatureSlideshow:', error);
    throw error;
  }
};

/**
 * Gets description based on feature image name
 * @param {string} imageName - Name of the image file
 * @returns {string} Feature description
 */
const getFeatureDescription = (imageName) => {
  const name = imageName?.toLowerCase();
  
  if (name?.includes('schedule')) {
    return 'Organize your day with intuitive scheduling tools and visual timeline views';
  } else if (name?.includes('calendar') || name?.includes('month')) {
    return 'Plan ahead with comprehensive monthly and yearly calendar overviews';
  } else if (name?.includes('habit') || name?.includes('tracker')) {
    return 'Build lasting habits with powerful tracking and progress visualization';
  } else if (name?.includes('goal')) {
    return 'Set and achieve your goals with structured planning and milestone tracking';
  } else if (name?.includes('journal') || name?.includes('note')) {
    return 'Capture thoughts and reflections with integrated journaling features';
  } else if (name?.includes('custom') || name?.includes('theme')) {
    return 'Personalize your planner with beautiful themes and custom layouts';
  } else if (name?.includes('pdf') || name?.includes('download')) {
    return 'Download your planner as a high-quality PDF ready for printing';
  } else if (name?.includes('mobile') || name?.includes('tablet')) {
    return 'Access your planner seamlessly on any device, anywhere you go';
  } else {
    return 'Discover powerful features designed to enhance your planning experience';
  }
};

/**
 * Fetches a PDF background image from the gallery bucket
 * @param {string} imageName - Name of the background image (e.g., 'pdf-background1.jpeg')
 * @returns {Promise<string>} Public URL of the image
 */
export const getPdfBackgroundImage = async (imageName = 'pdf-background1.jpeg') => {
  console.log('TRYING TO GENERATE BACKGROUND IMAGE');
  try {
    const { data: { publicUrl } } = supabase?.storage?.from('gallery')?.getPublicUrl(imageName);
    
    if (!publicUrl) {
      console.warn(`PDF background image ${imageName} not found in gallery bucket`);
      return null;
    }
    console.log(`THE BACKGROUND URL: ${publicUrl}`);
    return publicUrl;
  } catch (error) {
    console.error('Error fetching PDF background image:', error);
    return null;
  }
};

/**
 * Fetches all PDF background images from the gallery bucket
 * @returns {Promise<Array>} Array of PDF background image URLs
 */
export const getAllPdfBackgrounds = async () => {
  try {
    console.log('🔍 Fetching files from gallery bucket...');
    
    // Try listing with different options to handle potential subfolder structures
    const { data, error } = await supabase?.storage
      ?.from('gallery')
      ?.list('', {
        limit: 100,
        offset: 0,
        sortBy: { column: 'name', order: 'asc' }
      });

    if (error) {
      console.error('❌ Supabase error fetching PDF backgrounds:', error);
      
      // If list fails, try alternative approach: construct URLs directly
      console.log('🔄 Attempting alternative method with known filenames...');
      
      // Construct URLs for known pdf-background files (1-6)
      const backgroundImages = [];
      for (let i = 1; i <= 6; i++) {
        const filename = `pdf-background${i}.jpeg`;
        const { data: { publicUrl } } = supabase?.storage
          ?.from('gallery')
          ?.getPublicUrl(filename);
        
        if (publicUrl) {
          backgroundImages?.push({
            name: filename,
            url: publicUrl
          });
        }
      }
      
      console.log('✅ Alternative method found:', backgroundImages?.length, 'backgrounds');
      return backgroundImages;
    }

    console.log('📂 Total files in gallery bucket:', data?.length || 0);
    console.log('📋 All files:', data?.map(f => f?.name));

    // If we got data but it's empty, try the alternative method
    if (!data || data?.length === 0) {
      console.log('⚠️ List returned empty, trying alternative method...');
      
      const backgroundImages = [];
      for (let i = 1; i <= 6; i++) {
        const filename = `pdf-background${i}.jpeg`;
        const { data: { publicUrl } } = supabase?.storage
          ?.from('gallery')
          ?.getPublicUrl(filename);
        
        if (publicUrl) {
          backgroundImages?.push({
            name: filename,
            url: publicUrl
          });
        }
      }
      
      console.log('✅ Alternative method found:', backgroundImages?.length, 'backgrounds');
      return backgroundImages;
    }

    // Filter for PDF background images (pdf-background*.jpeg)
    const backgroundUrls = data
      ?.filter(file => {
        const isPdfBackground = file?.name?.toLowerCase()?.startsWith('pdf-background');
        const isJpeg = file?.name?.toLowerCase()?.endsWith('.jpeg');
        console.log(`Checking ${file?.name}: isPdfBackground=${isPdfBackground}, isJpeg=${isJpeg}`);
        return isPdfBackground && isJpeg;
      })
      ?.map(file => {
        const { data: { publicUrl } } = supabase?.storage
          ?.from('gallery')
          ?.getPublicUrl(file?.name);
        console.log(`✅ Generated URL for ${file?.name}:`, publicUrl);
        
        return {
          name: file?.name,
          url: publicUrl
        };
      });

    console.log('🎨 Filtered PDF backgrounds:', backgroundUrls?.length || 0);
    console.log('📋 Background URLs:', backgroundUrls);
    
    return backgroundUrls || [];
  } catch (error) {
    console.error('❌ Error in getAllPdfBackgrounds:', error);
    console.error('Error stack:', error?.stack);
    
    // Fallback: Try to construct URLs directly for known files
    console.log('🔄 Final fallback: constructing URLs for known files...');
    const backgroundImages = [];
    for (let i = 1; i <= 6; i++) {
      const filename = `pdf-background${i}.jpeg`;
      try {
        const { data: { publicUrl } } = supabase?.storage
          ?.from('gallery')
          ?.getPublicUrl(filename);
        
        if (publicUrl) {
          backgroundImages?.push({
            name: filename,
            url: publicUrl
          });
        }
      } catch (e) {
        console.error(`Failed to get URL for ${filename}:`, e);
      }
    }
    
    console.log('✅ Fallback method found:', backgroundImages?.length, 'backgrounds');
    return backgroundImages;
  }
};

/**
 * Fetches showcase planner images from the gallery bucket (pdf-sample- prefix)
 * @returns {Promise<Array>} Array of showcase image objects with URLs
 */
export const getShowcaseImages = async () => {
  try {
    console.log('🔍 [SHOWCASE] Starting to fetch showcase images from gallery bucket...');
    
    const { data, error } = await supabase?.storage?.from('gallery')?.list('', {
      limit: 100,
      offset: 0,
      sortBy: { column: 'name', order: 'asc' }
    });

    console.log('📦 [SHOWCASE] Raw response from Supabase:', { data, error });
    console.log('📊 [SHOWCASE] Data type:', typeof data);
    console.log('📊 [SHOWCASE] Data is array:', Array.isArray(data));
    console.log('📊 [SHOWCASE] Data length:', data?.length);
    console.log('📋 [SHOWCASE] All files in bucket:', data?.map(f => f?.name));

    if (error) {
      console.error('❌ [SHOWCASE] Supabase error fetching showcase images:', error);
      console.error('❌ [SHOWCASE] Error details:', JSON.stringify(error, null, 2));
      throw new Error(`Failed to fetch showcase images: ${error.message}`);
    }

    if (!data) {
      console.error('❌ [SHOWCASE] No data returned from Supabase (data is null/undefined)');
      throw new Error('No data returned from gallery bucket. Check bucket permissions and RLS policies.');
    }

    if (data?.length === 0) {
      console.warn('⚠️ [SHOWCASE] Gallery bucket returned empty array');
      console.warn('⚠️ [SHOWCASE] Possible causes:');
      console.warn('   1. No files in the bucket root folder');
      console.warn('   2. RLS policies blocking public access to list files');
      console.warn('   3. Bucket is private and requires authentication');
      throw new Error('Gallery bucket is empty or inaccessible. Verify files exist and bucket permissions allow public listing.');
    }

    console.log('🔎 [SHOWCASE] Filtering for pdf-sample-*.png files...');
    
    // Filter for PNG files with pdf-sample- prefix
    const showcaseImages = data
      ?.filter(file => {
        const hasPrefix = file?.name?.toLowerCase()?.startsWith('pdf-sample-');
        const isPng = file?.name?.toLowerCase()?.endsWith('.png');
        console.log(`   Checking "${file?.name}": hasPrefix=${hasPrefix}, isPng=${isPng}`);
        return hasPrefix && isPng;
      })
      ?.map(file => {
        const { data: { publicUrl } } = supabase?.storage?.from('gallery')?.getPublicUrl(file?.name);
        console.log(`   ✅ Generated URL for "${file?.name}": ${publicUrl}`);
        
        return {
          id: file?.name,
          name: file?.name,
          url: publicUrl,
          caption: 'Customized Planner Page' // Default caption, can be edited later
        };
      });

    console.log('✅ [SHOWCASE] Filtered showcase images count:', showcaseImages?.length);
    console.log('📋 [SHOWCASE] Final showcase images:', showcaseImages);

    if (showcaseImages?.length === 0) {
      console.warn('⚠️ [SHOWCASE] No files matched the filter criteria (pdf-sample-*.png)');
      console.warn('⚠️ [SHOWCASE] Files found in bucket:', data?.map(f => f?.name)?.join(', '));
      throw new Error('No showcase images found with prefix "pdf-sample-" and extension ".png". Check file naming in Supabase storage.');
    }

    return showcaseImages || [];
  } catch (error) {
    console.error('❌ [SHOWCASE] Error in getShowcaseImages:', error);
    console.error('❌ [SHOWCASE] Error stack:', error?.stack);
    throw error;
  }
};